import { ChartService, buildLineOption, type ChartConfig } from './ChartService';
import { Database, type TelemetrySample } from '../../store/Database';
import type { StateManager } from '../../core/StateManager';

interface TrendConfig {
  containerFreq: HTMLElement | null;
  containerCurrent: HTMLElement | null;
  containerTemp: HTMLElement | null;
  containerDC: HTMLElement | null;
  state: StateManager;
}

export class TrendDashboard {
  private charts: ChartService;
  private db: Database;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private history: TelemetrySample[] = [];
  private resizeObservers: ResizeObserver[] = [];

  constructor(
    private config: TrendConfig,
  ) {
    this.charts = new ChartService();
    this.db = new Database();

    queueMicrotask(() => {
      console.log('[TrendDashboard] Iniciando inicialização dos gráficos...');
      console.log('[TrendDashboard] Containers:', {
        freq: !!this.config.containerFreq,
        current: !!this.config.containerCurrent,
        temp: !!this.config.containerTemp,
        dc: !!this.config.containerDC,
      });
      
      this.initCharts()
        .then(() => {
          console.log('[TrendDashboard] Gráficos inicializados com sucesso');
          this.forceResizeAll();
          this.loadHistory();
        })
        .catch((err) => console.error('[TrendDashboard] Falha ao inicializar gráficos:', err));
    });
  }

  private forceResizeAll(): void {
    const chartIds = ['trend-freq', 'trend-current', 'trend-temp', 'trend-dc'];
    chartIds.forEach((id) => {
      const instance = this.charts.getInstance(id);
      if (instance) {
        try {
          instance.resize();
        } catch {
          // ignore resize errors
        }
      }
    });

    setTimeout(() => {
      chartIds.forEach((id) => {
        const instance = this.charts.getInstance(id);
        if (instance) {
          try {
            instance.resize();
          } catch {
            // ignore
          }
        }
      });
    }, 200);
  }

  start(autoRefreshMs = 5000): void {
    this.refreshTimer = setInterval(() => {
      this.loadHistory();
    }, autoRefreshMs);
  }

  stop(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private async initCharts(): Promise<void> {
    const chartConfigs: ChartConfig[] = [
      { id: 'trend-freq', type: 'line', title: 'Frequência (Hz)' },
      { id: 'trend-current', type: 'line', title: 'Corrente (A)' },
      { id: 'trend-temp', type: 'line', title: 'Temperatura (°C)' },
      { id: 'trend-dc', type: 'line', title: 'Tensão CC (V)' },
    ];

    const containers = [
      this.config.containerFreq,
      this.config.containerCurrent,
      this.config.containerTemp,
      this.config.containerDC,
    ];

    console.log('[TrendDashboard] Iniciando criação dos 4 gráficos...');
    
    const promises = chartConfigs.map(async (cfg, i) => {
      const container = containers[i];
      if (!container) {
        console.warn(`[TrendDashboard] Container ${cfg.id} não encontrado`);
        return;
      }

      console.log(`[TrendDashboard] Criando gráfico ${cfg.id}, container size:`, {
        width: container.clientWidth,
        height: container.clientHeight,
        display: getComputedStyle(container).display,
        visibility: getComputedStyle(container).visibility,
      });

      await this.charts.create(container, cfg);

      const resizeObserver = new ResizeObserver(() => {
        const instance = this.charts.getInstance(cfg.id);
        if (instance) {
          instance.resize();
        }
      });
      resizeObserver.observe(container);
      this.resizeObservers.push(resizeObserver);
      
      console.log(`[TrendDashboard] Gráfico ${cfg.id} criado com sucesso`);
    });
    await Promise.all(promises);

    this.renderInitialData();
  }

  private renderInitialData(): void {
    console.log('[TrendDashboard] Renderizando dados iniciais...');
    const state = this.config.state;
    const now = new Date();

    const initialSample: TelemetrySample = {
      timestamp: now,
      profileKey: state.get('selectedProfileKey'),
      frequency: state.get('currentFreq'),
      current: state.get('currentAmp'),
      dcVoltage: state.get('dcVoltage'),
      temperature: state.get('heatsinkTemp'),
      load: state.get('load'),
    };

    console.log('[TrendDashboard] Dados iniciais:', initialSample);

    this.history = [initialSample];
    this.updateCharts();
    console.log('[TrendDashboard] Dados iniciais renderizados');
  }

  private async loadHistory(): Promise<void> {
    try {
      const samples = await this.db.telemetry
        .orderBy('timestamp')
        .reverse()
        .limit(60)
        .toArray();

      this.history = samples.reverse();

      if (this.history.length === 0) return;

      this.updateCharts();
    } catch {
      // IndexedDB pode estar indisponível
    }
  }

  private updateCharts(): void {
    const timeLabels = this.history.map((s) =>
      s.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    );
    const showSymbol = this.history.length <= 2;

    this.charts.update(
      'trend-freq',
      buildLineOption(
        'Frequência (Hz)',
        timeLabels,
        [{ name: 'Frequência Real', data: this.history.map((s) => s.frequency), color: '#00d9ff' }],
        showSymbol,
      ),
    );

    this.charts.update(
      'trend-current',
      buildLineOption(
        'Corrente (A)',
        timeLabels,
        [{ name: 'Corrente Motor', data: this.history.map((s) => s.current), color: '#00ff9d' }],
        showSymbol,
      ),
    );

    this.charts.update(
      'trend-temp',
      buildLineOption(
        'Temperatura (°C)',
        timeLabels,
        [{ name: 'Dissipador', data: this.history.map((s) => s.temperature), color: '#ffb700' }],
        showSymbol,
      ),
    );

    this.charts.update(
      'trend-dc',
      buildLineOption(
        'Tensão CC (V)',
        timeLabels,
        [{ name: 'Barramento CC', data: this.history.map((s) => s.dcVoltage), color: '#ff4757' }],
        showSymbol,
      ),
    );
  }

  addSample(sample: TelemetrySample): void {
    this.history.push(sample);
    if (this.history.length > 60) {
      this.history.shift();
    }
    this.updateCharts();
  }

  destroy(): void {
    this.stop();
    this.resizeObservers.forEach((ro) => ro.disconnect());
    this.resizeObservers = [];
    this.charts.disposeAll();
  }
}
